import axios, { AxiosError } from "axios";
import { API_BASE } from "./env";
import type { ApiEnvelope, ApiError, AuthMe, AuthResult, PlatformMeta } from "@/types";

export const http = axios.create({ baseURL: API_BASE, withCredentials: true });

http.interceptors.request.use((config) => {
  const token = localStorage.getItem("resfil_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

export function formatApiError(error: unknown): string {
  const ax = error as AxiosError<{ error?: ApiError }>;
  const apiErr = ax?.response?.data?.error;
  if (apiErr?.message) return apiErr.message;
  if (ax?.message) return ax.message;
  return "Something went wrong. Please try again.";
}

/** The API's error code (for example "chat_ended"), when the server sent one. */
export function apiErrorCode(error: unknown): string | undefined {
  return (error as AxiosError<{ error?: ApiError }>)?.response?.data?.error?.code;
}

async function unwrap<T>(p: Promise<{ data: ApiEnvelope<T> }>): Promise<T> {
  const res = await p;
  return res.data.data;
}

export interface MfaStatus {
  enabled: boolean;
  required: boolean;
  backup_codes_remaining: number;
  pending_setup: boolean;
}

export interface Profile {
  id: string; first_name: string; last_name: string; email: string;
  phone?: string | null; avatar_url?: string | null; timezone?: string | null; email_verified?: boolean;
}
export interface OrganizationFull {
  id: string; name: string; legal_name?: string; slug: string; industry: string;
  website?: string | null; primary_email?: string | null; primary_phone?: string | null;
  timezone?: string | null; status?: string; onboarding_status?: string;
}
export interface Member {
  id: string; role: string; invitation_status: string; invited_email?: string | null;
  profile?: { first_name?: string; last_name?: string; email?: string; avatar_url?: string | null };
}

export interface OnboardingState {
  business_profile: Record<string, unknown> | null;
  location: Record<string, unknown> | null;
  hours: Record<string, unknown>[];
  services: Record<string, unknown>[];
  receptionist: Record<string, unknown> | null;
  faqs: Record<string, unknown>[];
  steps: Record<string, boolean>;
  completed_count: number;
  current_step: string;
  onboarding_status: string;
}
export interface ReadinessCheck {
  key: string; label: string; done: boolean; pending?: boolean;
  state?: string; detail?: string; route?: string; action?: string;
}
export interface ReadinessState {
  checks: ReadinessCheck[];
  setup_complete: boolean;
  integrations: Record<string, string>;
}

export interface DashboardData {
  onboarding_status: string;
  receptionist: { status: string; name: string | null; configured: boolean };
  integrations: { key: string; label: string; provider?: string; status: string; detail?: string }[];
  metrics: {
    calls_answered_today: number;
    sms_conversations_today: number;
    new_leads_today: number;
    appointments_booked_today: number;
  };
  upcoming_appointments: Record<string, unknown>[];
  recent_conversations: Record<string, unknown>[];
  recent_activity: Record<string, unknown>[];
  human_assistance_requests: Record<string, unknown>[];
  generated_at: string;
}

export interface KnowledgeEntry {
  id: string; organization_id: string; category: string; title: string; content: string;
  approval_status: "draft" | "approved" | "archived";
  customer_visible: boolean; internal_only: boolean;
  effective_at?: string | null; expires_at?: string | null;
  created_by?: string; approved_by?: string | null; approved_at?: string | null;
  created_at: string; updated_at: string;
}
export interface KnowledgeDocument {
  id: string; organization_id: string; original_filename: string; content_type: string;
  extension: string; size: number; storage_path: string;
  processing_status: "uploaded" | "processing" | "ready" | "failed";
  processing_error?: string | null; chunk_count: number; ai_ready: boolean;
  created_at: string; updated_at: string;
}
export interface RetrievalSource {
  source_type: string; entry_id: string; title: string; category: string;
  content: string; snippet: string; effective_at?: string | null; expires_at?: string | null;
  approved_by?: string | null;
}
export interface RetrievalResult {
  query: string; count: number; sources: RetrievalSource[]; eligible_pool_size: number;
  documents: { uploaded: number; ai_ready: number; note: string };
  ai_answer: null; note: string; generated_at: string;
}
export interface KnowledgeCategory { value: string; label: string; }

export interface CalendarConnection {
  connected: boolean; configured: boolean; status: string;
  google_email: string | null; calendar_id: string | null; calendar_summary: string | null;
  last_sync_at: string | null; last_sync_status: string | null; last_error: string | null; connected_at: string | null;
}
export interface GoogleCalendarItem { id: string; summary: string; primary: boolean; access_role: string; }
export interface CalendarSyncLog { id: string; appointment_id: string; action: string; ok: boolean; error: string | null; created_at: string; }

export interface InboxConversation {
  id: string; channel: string; status: string; ai_active: boolean; assigned_to: string | null;
  contact_id: string | null; lead_id: string | null; appointment_id: string | null;
  subject: string; customer_name: string | null; customer_phone: string | null;
  contact_name?: string; contact_phone?: string | null;
  unread_count: number; last_message_at: string; last_message_preview: string | null;
  last_message_kind: string | null; spam: boolean; created_at: string; updated_at: string;
}
export interface InboxMessage {
  id: string; conversation_id: string; kind: string; body: string; author: string;
  metadata: Record<string, unknown>; created_at: string; customer_visible: boolean; deliverable: boolean;
}
export interface InboxDetail {
  conversation: InboxConversation; messages: InboxMessage[];
  call_record: Record<string, unknown> | null; escalations: Record<string, unknown>[];
  ai_actions: Record<string, unknown>[]; tags: Record<string, unknown>[];
  contact: Record<string, unknown> | null; lead: Record<string, unknown> | null;
  appointment: Record<string, unknown> | null;
}

export interface AiInteraction {
  id: string; conversation_id: string; intent: string; confidence: number | null;
  knowledge_sources: { title: string; snippet: string; category?: string }[];
  proposed_action: string; executed_actions: { name: string; arguments: Record<string, unknown>; result: Record<string, unknown> }[];
  tool_results: unknown[]; escalation_reason: string | null; response: string | null;
  escalated: boolean; created_at: string;
}
export interface ReceptionistTurn {
  conversation_id: string; response: string | null; intent: string | null; confidence: number | null;
  escalated: boolean; ai_paused: boolean; executed_actions: AiInteraction["executed_actions"];
  sources: AiInteraction["knowledge_sources"]; interaction_id?: string;
}

export interface SimProposedAction { id: string; name: string; arguments: Record<string, unknown>; simulated_result: Record<string, unknown>; decision: string; }
export interface SimTurn {
  id: string; customer_text: string; ai_response: string; intent: string; confidence: number;
  proposed_action: string; knowledge_sources: { title: string; snippet: string }[];
  contact_changes: Record<string, unknown>[]; lead_changes: Record<string, unknown>[];
  proposed_appointment_times: { start_at: string; local_time: string }[];
  proposed_tool_calls: SimProposedAction[]; escalation_reason: string | null; created_at: string;
}
export interface SimChecklistItem { key: string; label: string; auto: string; override: string | null; status: string; manual: boolean; }
export interface SimSession {
  id: string; scenario_key: string; scenario_title: string; config_name: string; opening: string;
  turns: SimTurn[]; checklist: SimChecklistItem[]; overall_status: string; updated_at: string;
}
export interface SimScenario { key: string; title: string; opening: string; checklist: { key: string; label: string }[]; }

const ONB = "/api/v1/onboarding";
const KB = "/api/v1/knowledge";
const SVC = "/api/v1/services";
const DEMO = "/api/v1/demo";
export interface ImportPreview {  total_rows: number; valid: number; to_create: number; to_update: number;
  with_lead: number; error_count: number; errors: { row: number; error: string }[]; max_rows: number;
}
export interface ImportResult {
  id: string; filename: string; total_rows: number; created: number; updated: number;
  leads_created: number; error_count: number; errors: { row: number; error: string }[]; created_at: string;
}

export interface RecoveryStep {
  channel: string; state: string; result: string | null; run_at?: string;
  attempts?: number; offset_min?: number; sent_at?: string;
}
export interface RecoveryConfig {
  plan: string; sms_configured: boolean; voice_recovery_available: boolean;
  voice_recovery_note: string; max_steps: number; editable: boolean;
  steps: RecoveryStep[]; states: string[];
}
export interface RecoveryCampaign {
  id: string; lead_id: string; contact_id?: string; status: string; plan: string;
  steps: RecoveryStep[]; current_step: number; stop_reason?: string; created_at: string;
}
export interface RecoveryMetrics {
  leads_entered: number; recovery_attempts: number; recovered_leads: number;
  appointments_recovered: number; recovery_rate: number;
  range: { start: string; end: string; filter: string };
}

export interface EstimateStep {
  channel: string; state: string; result: string | null; run_at?: string; message?: string;
  ai_generated?: boolean; attempts?: number; sent_at?: string | null; delivery_status?: string | null;
}
export interface EstimateFollowup {
  enabled: boolean; status: string; plan?: string; ai_enabled?: boolean;
  steps: EstimateStep[]; current_step?: number; stop_reason?: string | null;
}
export interface Estimate {
  id: string; lead_id?: string; contact_id?: string; amount: number | null; amount_disclosed: boolean;
  service?: string | null; description?: string | null; valid_until?: string | null; assigned_to?: string | null;
  status: string; sent_at?: string | null; followup: EstimateFollowup; contact_name?: string;
  contact_phone?: string | null; next_followup_at?: string | null; followup_state?: string; created_at: string;
}
export interface EstimateConfig {
  plan: string; followup_included: boolean; configurable: boolean; ai_available: boolean; ai_entitled: boolean;
  max_steps: number; sms_configured: boolean; default_steps: { offset_min: number; channel: string }[];
  steps: { offset_min: number; channel: string }[]; statuses: string[];
  integration_available: boolean; integration_note: string;
}
export interface EstimateMetrics {
  total: number; by_status: Record<string, number>; active_followups: number;
  win_rate: number; accepted: number; declined: number;
}

const CRM = "/api/v1/crm";const SCHED = "/api/v1/scheduling";

export interface AvailabilitySlot { start_at: string; end_at: string; local_time: string; }export interface AvailabilityResult {
  slots: AvailabilitySlot[]; timezone: string; out_of_service_area: boolean;
  appointment_type: Record<string, unknown>;
}
export interface Appointment {
  id: string; organization_id: string; appointment_type_id: string; service_id?: string | null;
  contact_id?: string | null; contact_name?: string; lead_id?: string | null;
  requested_service?: string | null; service_name?: string | null;
  start_at: string; end_at: string; status: string; blocks_slot: boolean;
  intake_answers?: Record<string, unknown>; notes?: string | null; timezone?: string | null;
  source?: string; cancel_reason?: string | null; assigned_technician_id?: string | null;
  assigned_at?: string | null; dispatch_auto_assigned?: boolean; job_status?: string | null;
  eta_window_start?: string | null; eta_window_end?: string | null; job_status_history?: JobStatusEntry[];
  created_at: string; updated_at: string;
}

// Smart Dispatch Recommendations
export interface DispatchConfig {
  plan: string; included: boolean; auto_routing_available: boolean; auto_assign: boolean;
}
export interface DispatchTechnician {
  user_id: string; name: string; email?: string; role?: string; skills: string[]; territories: string[];
  active: boolean; max_daily_jobs: number | null; has_profile: boolean;
}
export interface DispatchRecommendation {
  technician_id: string; name: string; score: number; available: boolean; over_capacity: boolean;
  eligible: boolean; reasons: string[];
}
export interface DispatchRecommendations {
  appointment_id: string; context: Record<string, unknown>; recommendations: DispatchRecommendation[];
  recommended: DispatchRecommendation | null; assigned_technician_id: string | null; auto_assign: boolean;
}

// Technician ETA & On-the-Way Notifications
export interface EtaConfig {
  plan: string; included: boolean; eta_window: boolean; location_aware_available: boolean;
  location_tracking_status: string; sms_configured: boolean; statuses: string[];
  notify: Record<string, boolean>; messages: Record<string, string>; default_messages: Record<string, string>;
}
export interface EtaNotification { status: string; reason?: string; preview?: string; sid?: string; }
export interface JobStatusEntry {
  status: string; actor: string; at: string; eta_window_start: string | null; eta_window_end: string | null;
  notification: EtaNotification | null;
}

// Contractor Integration Framework (marketplace)
export interface IntegrationField { key: string; label: string; secret: boolean; }
export interface IntegrationConnState { status: string; last_verified_at: string | null; detail: string | null; sync_status?: string; }
export interface IntegrationProvider {
  key: string; name: string; category: string; auth_type: string; min_plan: string;
  fields: IntegrationField[]; sync: string[]; managed_note?: string | null; available: boolean;
  connection: IntegrationConnState;
}
export interface IntegrationCatalog { plan: string; sync_entities: string[]; providers: IntegrationProvider[]; }

// Multi-Location Routing
export interface LocationConfig {
  plan: string; max_locations: number; routing_enabled: boolean; markets_enabled: boolean;
  advanced_rules: boolean; location_count: number;
}
export interface BranchLocation {
  id: string; name: string; market: string | null; phone_number: string | null; zip_codes: string[];
  service_types: string[]; priority: number; greeting: string | null;
  business_open_hour: number | null; business_close_hour: number | null; active: boolean; is_default: boolean;
}
export interface RouteResult {
  location: BranchLocation; reasons: string[]; candidates: number; routing_enabled: boolean;
  alternatives?: { id: string; name: string; score: number }[];
}

// Customer Portal (public tokenized view)
export interface PublicPortalAppointment {
  service: string | null; start_at: string; date_display: string; time_display: string; status: string;
  timezone: string | null; can_reschedule: boolean; manage_url: string | null;
}
export interface AttributionData {
  plan: string; entitlements: { summary: boolean; estimated: boolean; actual: boolean; custom: boolean };
  funnel: { leads: number; booked: number; completed: number; actual_revenue: number | null };
  revenue_integrated: boolean; revenue_note: string | null;
  attribution: { category: string; leads: number; estimated_pipeline: number }[] | null;
  services: { service: string; completed_jobs: number }[] | null;
  actual_revenue?: number; actual_deals?: number; custom_reporting?: boolean; basic_only?: boolean;
}

export interface LostLeadItem {
  id: string; detected_at: string; channel: string; signal: string | null;
  reason: string; reason_source: string; rationale: string | null;
  estimated_value: number | null; contact_name: string | null; contact_phone: string | null;
  summary: string; conversation_id: string | null;
}
export interface LostLeadData {
  available: boolean; plan: string; min_plan?: string; message?: string;
  entitlements?: Record<string, boolean>;
  range?: { start: string; end: string; filter: string };
  total?: number; by_channel?: Record<string, number>; llm_configured?: boolean;
  basic_only?: boolean;
  reasons?: { reason: string; label: string; count: number }[];
  unclassified_count?: number;
  trend?: { date: string; count: number }[];
  estimated_impact?: number; impact_missing_count?: number; impact_note?: string;
  items?: LostLeadItem[]; custom_analysis?: boolean;
}
export interface LostLeadMeta {
  plan: string; entitlements: Record<string, boolean>;
  reasons: { value: string; label: string }[]; llm_configured: boolean;
}

export interface QualityCheck { key: string; label: string; passed: boolean; note?: string; }
export interface QualityCriterion { key: string; label: string; passed: number; evaluated: number; pass_rate: number | null; }
export interface QualityItem {
  id: string; conversation_id: string; score: number | null; channel: string | null; detected_at: string;
  contact_name: string | null; contact_phone: string | null;
  passed_checks: QualityCheck[]; failed_checks: QualityCheck[];
}
export interface QualitySuggestion {
  id: string; conversation_id: string; title: string; content: string; category: string; rationale: string; status: string;
}
export interface QualityData {
  available: boolean; plan: string; min_plan?: string; message?: string;
  entitlements?: Record<string, boolean>; range?: { start: string; end: string; filter: string };
  llm_configured?: boolean; evaluated_count?: number; unevaluated_count?: number; average_score?: number | null;
  criteria?: QualityCriterion[]; items?: QualityItem[]; suggestions?: QualitySuggestion[];
  pending_suggestions_count?: number; custom_rubric?: boolean;
}
export interface QualityMeta {
  plan: string; entitlements: Record<string, boolean>;
  criteria: { key: string; label: string; weight: number }[];
  default_criteria: { key: string; label: string; weight: number }[]; llm_configured: boolean;
}

export interface ForecastData {
  available: boolean; plan: string; entitlements: Record<string, boolean>;
  timezone: string; lookback_days: number;
  thresholds: { min_days: number; min_calls: number };
  historical: {
    busiest_days: { label: string; count: number }[];
    busiest_hours: { label: string; count: number }[];
    after_hours_calls: number; total_calls: number; active_days: number;
    daily_series: { date: string; count: number }[];
  };
  service_trends?: { service: string; total: number; recent: number; earlier: number; direction: string }[];
  has_enough_data?: boolean; insufficient_reason?: string;
  horizon_days?: number; forecast_label?: string;
  forecast?: {
    weekday_averages: { weekday: string; avg_calls: number }[];
    upcoming: { date: string; weekday: string; predicted_calls: number }[];
    coverage_windows: { start: string; end: string; avg_calls_per_day: number }[];
    busiest_day: string | null; busiest_hour: string | null; method: string;
  } | null;
  custom_forecasting?: boolean;
}

export interface OptimizationRec {
  key: string; category: string; title: string; evidence: string;
  expected_impact: string | null; severity: "high" | "medium" | "low"; deep_link: string;
  apply_supported: boolean; apply_note: string | null; meta: Record<string, unknown>;
  status: "pending" | "reviewed" | "dismissed" | "applied"; applied_result?: Record<string, unknown> | null;
}
export interface OptimizationData {
  available: boolean; plan: string;
  entitlements: { available: boolean; level: string; apply: boolean; custom_rules: boolean };
  recommendations: OptimizationRec[];
  counts: { total: number; pending: number };
}

export interface TelephonyGoLiveStep {
  key: string; label: string; status: "done" | "in_progress" | "todo"; auto: boolean;
  detail?: string; value?: string;
}
export interface TelephonyGoLive {
  overall: "live" | "configuration_required" | "not_configured"; message: string; ready: boolean;
  kyc_status: string; a2p_status: string; steps: TelephonyGoLiveStep[]; updated_at: string | null;
}

export interface PublicPortal {
  customer_name: string;
  business: { name: string | null; phone: string | null; email: string | null; website: string | null; address: string | null; hours: unknown };
  services: { name: string; description: string | null; pricing_type: string | null; price: number | null }[];
  appointments: PublicPortalAppointment[];
  estimates: { supported: boolean; status: string; items: unknown[] };
}
export interface BlockedTime { id: string; start_at: string; end_at: string; reason?: string | null; created_at: string; }

export interface PublicAppointment {
  business_name: string; service: string | null; start_at: string; date_display: string;
  time_display: string; timezone?: string; status: string; appointment_type_id?: string;
  can_manage: boolean; can_confirm?: boolean; can_cancel?: boolean; can_reschedule?: boolean; is_no_show?: boolean;
}
export interface NoShowStep {
  channel: string; state: string; result: string | null; run_at?: string; message?: string;
  ai_generated?: boolean; attempts?: number; sent_at?: string | null;
}
export interface NoShowCampaign {
  id: string; appointment_id: string; contact_id?: string; status: string; plan: string; assigned_to?: string | null;
  steps: NoShowStep[]; stop_reason?: string | null; contact_name?: string; service?: string | null;
  missed_at?: string | null; customer_link?: string | null; created_at: string;
}
export interface NoShowConfig {
  plan: string; included: boolean; max_steps: number; ai_entitled: boolean; ai_available: boolean;
  sms_configured: boolean; two_way_ai_available: boolean; steps: { offset_min: number; channel: string }[];
}
export interface NoShowMetrics { no_shows: number; recovery_attempts: number; rescheduled: number; recovery_rate: number; campaigns: number; }
export interface OrgMember { id: string; name: string; email?: string; role?: string; }

export interface CampaignStats { total: number; sent: number; unavailable: number; skipped: number; pending: number; }
export interface Campaign {
  id: string; name: string; message: string; channel: string; status: string;
  contact_ids: string[]; scheduled_at?: string | null; stats: CampaignStats;
  audience_counts?: Record<string, number>; created_at: string;
}
export interface CampaignTemplate { key: string; name: string; message: string; }
export interface CampaignConfig {
  plan: string; included: boolean; max_active: number; max_audience: number; voice_available: boolean;
  sms_configured: boolean; active_count: number; templates: CampaignTemplate[];
}
export interface AudiencePreview {
  selected: number; eligible: number; opted_out: number; no_phone: number; truncated: boolean; max_audience: number;
}

export interface OutboundCall {
  id: string; contact_id?: string; contact_name?: string; phone: string; purpose: string; status: string;
  outcome?: string | null; transcript_ref?: string | null; extracted_result?: unknown; provider?: string | null;
  provider_call_id?: string | null; attempts?: number; created_at: string;
}
export interface OutboundConfig {
  plan: string; included: boolean; allowance: number; usage_this_month: number; provider: string;
  provider_configured: boolean; telephony_configured: boolean; purposes: string[];
  timezone?: string; window_start_hour?: number; window_end_hour?: number;
}
export interface ReminderStep {
  kind: string; channel: string; hours_before: number | null; state: string; run_at?: string;
  message?: string; ai_generated?: boolean; attempts?: number; result: string | null; sent_at?: string | null;
}
export interface ReminderDoc {
  id: string; appointment_id: string; plan: string; status: string; steps: ReminderStep[]; stop_reason?: string | null;
}
export interface ReminderConfig {
  plan: string; configurable: boolean; max_reminders: number; voice_available: boolean; ai_entitled: boolean;
  ai_available: boolean; sms_configured: boolean; reminders: { hours_before: number; channel: string }[];
  default: { hours_before: number; channel: string }[]; confirmation_included: boolean;
}

// Automated Review Generation
export interface ReviewConfig {
  plan: string; enabled: boolean; sequences: boolean; analytics: boolean; max_steps: number;
  sms_configured: boolean; google_review_url: string | null; message: string; delay_hours: number;
  internal_feedback_enabled: boolean;
}
export interface ReviewRequest {
  id: string; status: string; attempts: number; result: string | null; sent_at: string | null;
  clicked_at: string | null; feedback: { rating: number; comment: string | null; at: string } | null; created_at: string;
}
export interface ReviewMetrics {
  requests_sent: number; requested: number; review_link_clicks: number; feedback_received: number;
  range: { start: string; end: string; filter: string };
}
export interface PublicReview {
  business_name: string; google_review_url: string | null; internal_feedback_enabled: boolean; already_submitted: boolean;
}

// Maintenance Membership Promotion
export interface MembershipConfig {
  plan: string; included: boolean; analytics: boolean; max_plans: number; plan_count: number;
}
export interface MembershipPlan {
  id: string; name: string; description: string | null; price: number | null; price_interval: string;
  benefits: string[]; eligibility: string | null; talking_points: string[]; active: boolean;
  billing: string; billing_link: string | null; created_at: string;
}
export interface MembershipHistory {
  stage: string; source: string; actor: string; conversation_id: string | null; note: string | null; at: string;
}
export interface MembershipEngagement {
  id: string; membership_plan_id: string; membership_plan_name: string; contact_id: string | null;
  contact_name?: string; stage: string; last_source: string; last_conversation_id: string | null;
  history: MembershipHistory[]; created_at: string; updated_at: string;
}
export interface MembershipMetrics {
  funnel: Record<string, number>; total_engaged: number; enroll_conversion_rate: number; analytics: boolean;
  per_plan?: { plan_id: string; name: string; offered: number; interested: number; follow_up_requested: number; enrolled: number }[];
  range: { start: string; end: string; filter: string };
}

// Seasonal Campaign Automation
export interface SeasonalPreset { season_key: string; name: string; message: string; }
export interface SeasonalConfig {
  plan: string; included: boolean; recurring: boolean; advanced_segments: boolean; max_rules: number;
  rules_count: number; sms_configured: boolean; presets: SeasonalPreset[];
}
export interface SeasonalTemplate {
  id: string; name: string; message: string; season_key: string; channel: string; created_at: string;
}
export interface SeasonalAudience { type: string; contact_ids?: string[]; filters?: Record<string, unknown>; }
export interface SeasonalRule {
  id: string; name: string; message: string; channel: string; schedule_type: string; run_date: string | null;
  annual_month: number | null; annual_day: number | null; lead_days: number; audience: SeasonalAudience;
  recurring: boolean; status: string; recurring_authorized: boolean; authorized_by: string | null;
  authorized_at: string | null; audit?: unknown[]; created_at: string;
}
export interface SeasonalStats { total: number; sent: number; unavailable: number; skipped: number; pending: number; }
export interface SeasonalOccurrence {
  id: string; rule_id: string | null; name: string; message: string; channel: string; season_key: string | null;
  status: string; scheduled_for: string; stats: SeasonalStats; excluded?: unknown[]; generated_by: string;
  authorized_by: string | null; authorized_at: string | null; audience_snapshot?: Record<string, unknown>; created_at: string;
}
export interface SeasonalCalendar {
  occurrences: SeasonalOccurrence[];
  projections: { rule_id: string; name: string; scheduled_for: string; authorized: boolean; projected: boolean }[];
}
export interface SeasonalMetrics {
  campaigns: number; completed: number; pending_authorization: number; messages_sent: number;
  messages_unavailable: number; range: { start: string; end: string; filter: string };
}

export const api = {
  meta: () => unwrap<PlatformMeta>(http.get("/api/meta")),

  getDashboard: () => unwrap<DashboardData>(http.get("/api/v1/dashboard")),

  kbCategories: () => unwrap<KnowledgeCategory[]>(http.get(`${KB}/categories`)),
  kbList: (params: { category?: string; status?: string; q?: string }) =>
    unwrap<KnowledgeEntry[]>(http.get(`${KB}/entries`, { params })),
  kbGet: (id: string) => unwrap<KnowledgeEntry>(http.get(`${KB}/entries/${id}`)),
  kbCreate: (b: Record<string, unknown>) => unwrap<KnowledgeEntry>(http.post(`${KB}/entries`, b)),
  kbUpdate: (id: string, b: Record<string, unknown>) => unwrap<KnowledgeEntry>(http.put(`${KB}/entries/${id}`, b)),
  kbApprove: (id: string) => unwrap<KnowledgeEntry>(http.post(`${KB}/entries/${id}/approve`)),
  kbArchive: (id: string) => unwrap<KnowledgeEntry>(http.post(`${KB}/entries/${id}/archive`)),
  kbDraft: (id: string) => unwrap<KnowledgeEntry>(http.post(`${KB}/entries/${id}/draft`)),
  kbDocuments: () => unwrap<KnowledgeDocument[]>(http.get(`${KB}/documents`)),
  kbUpload: (file: File) => {
    const fd = new FormData();
    fd.append("file", file);
    return unwrap<KnowledgeDocument>(http.post(`${KB}/documents`, fd, { headers: { "Content-Type": "multipart/form-data" } }));
  },
  kbDeleteDocument: (id: string) => unwrap<{ deleted: string }>(http.delete(`${KB}/documents/${id}`)),
  kbRetryDocument: (id: string) => unwrap<Record<string, unknown>>(http.post(`${KB}/documents/${id}/retry`)),
  kbRetrieve: (question: string) => unwrap<RetrievalResult>(http.post(`${KB}/retrieve`, { question })),
  kbAiContextTest: (question: string, mode: string, location?: string) =>
    unwrap<any>(http.post(`${KB}/ai-context-test`, { question, mode, location })),

  // Sales Demo Mode (fully isolated)
  demoGet: () => unwrap<{ active: boolean; session: Record<string, unknown> | null; steps: Record<string, unknown>[] }>(http.get(`${DEMO}`)),
  demoStart: () => unwrap<Record<string, unknown>>(http.post(`${DEMO}/start`)),
  demoReset: () => unwrap<Record<string, unknown>>(http.post(`${DEMO}/reset`)),
  demoNext: () => unwrap<Record<string, unknown>>(http.post(`${DEMO}/guided/next`)),
  demoExit: () => unwrap<{ active: boolean }>(http.post(`${DEMO}/exit`)),

  // CRM (Contacts & Leads)
  crmStages: () => unwrap<{ value: string; label: string }[]>(http.get(`${CRM}/stages`)),
  crmContacts: (params: Record<string, string> = {}) => unwrap<Record<string, unknown>[]>(http.get(`${CRM}/contacts`, { params })),
  crmContactGet: (id: string) => unwrap<Record<string, unknown>>(http.get(`${CRM}/contacts/${id}`)),
  crmContactCreate: (b: Record<string, unknown>) => unwrap<Record<string, unknown>>(http.post(`${CRM}/contacts`, b)),
  crmContactUpdate: (id: string, b: Record<string, unknown>) => unwrap<Record<string, unknown>>(http.put(`${CRM}/contacts/${id}`, b)),
  crmContactArchive: (id: string) => unwrap<Record<string, unknown>>(http.post(`${CRM}/contacts/${id}/archive`)),
  crmCheckDuplicate: (b: { phone?: string; email?: string }) => unwrap<{ possible_duplicates: Record<string, unknown>[]; count: number }>(http.post(`${CRM}/contacts/check-duplicate`, b)),
  crmLeads: (params: Record<string, string> = {}) => unwrap<Record<string, unknown>[]>(http.get(`${CRM}/leads`, { params })),
  crmBoard: () => unwrap<{ stages: { value: string; label: string }[]; columns: Record<string, Record<string, unknown>[]> }>(http.get(`${CRM}/leads/board`)),
  crmLeadGet: (id: string) => unwrap<Record<string, unknown>>(http.get(`${CRM}/leads/${id}`)),
  crmLeadCreate: (b: Record<string, unknown>) => unwrap<Record<string, unknown>>(http.post(`${CRM}/leads`, b)),
  crmLeadUpdate: (id: string, b: Record<string, unknown>) => unwrap<Record<string, unknown>>(http.put(`${CRM}/leads/${id}`, b)),
  crmLeadStage: (id: string, stage: string, lost_reason?: string) => unwrap<Record<string, unknown>>(http.post(`${CRM}/leads/${id}/stage`, { stage, lost_reason })),
  crmLeadArchive: (id: string) => unwrap<Record<string, unknown>>(http.post(`${CRM}/leads/${id}/archive`)),
  crmLeadNote: (id: string, body: string) => unwrap<Record<string, unknown>>(http.post(`${CRM}/leads/${id}/notes`, { body })),
  crmImportPreview: (file: File) => {
    const fd = new FormData(); fd.append("file", file);
    return unwrap<ImportPreview>(http.post(`${CRM}/import/preview`, fd, { headers: { "Content-Type": "multipart/form-data" } }));
  },
  crmImportRun: (file: File) => {
    const fd = new FormData(); fd.append("file", file);
    return unwrap<ImportResult>(http.post(`${CRM}/import`, fd, { headers: { "Content-Type": "multipart/form-data" } }));
  },
  crmImportHistory: () => unwrap<ImportResult[]>(http.get(`${CRM}/import/history`)),
  crmImportTemplateUrl: () => `${API_BASE}${CRM}/import/template`,

  // Lead Recovery Engine
  recoveryConfig: () => unwrap<RecoveryConfig>(http.get(`/api/v1/recovery/config`)),
  recoveryMetrics: (date_filter = "last_30_days") => unwrap<RecoveryMetrics>(http.get(`/api/v1/recovery/metrics`, { params: { date_filter } })),
  recoveryCampaigns: () => unwrap<RecoveryCampaign[]>(http.get(`/api/v1/recovery/campaigns`)),
  recoveryEnroll: (lead_id: string) => unwrap<RecoveryCampaign>(http.post(`/api/v1/recovery/enroll`, { lead_id })),
  recoveryStop: (lead_id: string) => unwrap<{ stopped: boolean }>(http.post(`/api/v1/recovery/stop`, { lead_id })),
  recoveryProcess: () => unwrap<{ campaigns_scanned: number; details: unknown[] }>(http.post(`/api/v1/recovery/process`)),

  // Estimate & Quote Follow-Up
  estimatesConfig: () => unwrap<EstimateConfig>(http.get(`/api/v1/estimates/config`)),
  estimatesConfigSet: (steps: unknown[]) => unwrap<{ steps: unknown[] }>(http.put(`/api/v1/estimates/config`, { steps })),
  estimatesList: (params: Record<string, string> = {}) => unwrap<Estimate[]>(http.get(`/api/v1/estimates`, { params })),
  estimatesMetrics: () => unwrap<EstimateMetrics>(http.get(`/api/v1/estimates/metrics`)),
  estimateCreate: (b: Record<string, unknown>) => unwrap<Estimate>(http.post(`/api/v1/estimates`, b)),
  estimateUpdate: (id: string, b: Record<string, unknown>) => unwrap<Estimate>(http.put(`/api/v1/estimates/${id}`, b)),
  estimateStatus: (id: string, status: string) => unwrap<Estimate>(http.post(`/api/v1/estimates/${id}/status`, { status })),
  estimateFollowupStart: (id: string, ai_followup = false) => unwrap<Estimate>(http.post(`/api/v1/estimates/${id}/followup/start`, { ai_followup })),
  estimateFollowupStop: (id: string) => unwrap<{ stopped: string }>(http.post(`/api/v1/estimates/${id}/followup/stop`)),
  estimatesProcess: () => unwrap<{ expired: number; estimates_scanned: number; details: unknown[] }>(http.post(`/api/v1/estimates/process`)),
  crmTags: () => unwrap<Record<string, unknown>[]>(http.get(`${CRM}/tags`)),
  crmTagCreate: (name: string, color = "slate") => unwrap<Record<string, unknown>>(http.post(`${CRM}/tags`, { name, color })),
  crmLeadTag: (id: string, tag_id: string) => unwrap<Record<string, unknown>[]>(http.post(`${CRM}/leads/${id}/tags`, { tag_id })),
  crmLeadUntag: (id: string, tag_id: string) => unwrap<Record<string, unknown>[]>(http.delete(`${CRM}/leads/${id}/tags/${tag_id}`)),

  // Services module
  svcList: (activeOnly?: boolean) => unwrap<Record<string, unknown>[]>(http.get(`${SVC}/services`, { params: activeOnly ? { active: "true" } : {} })),
  svcCreate: (b: Record<string, unknown>) => unwrap<Record<string, unknown>>(http.post(`${SVC}/services`, b)),
  svcUpdate: (id: string, b: Record<string, unknown>) => unwrap<Record<string, unknown>>(http.put(`${SVC}/services/${id}`, b)),
  svcDelete: (id: string) => unwrap<{ deleted: string }>(http.delete(`${SVC}/services/${id}`)),
  svcDemoSeed: () => unwrap<{ created: Record<string, unknown>[]; count: number }>(http.post(`${SVC}/demo-seed`)),
  atList: () => unwrap<Record<string, unknown>[]>(http.get(`${SVC}/appointment-types`)),
  atCreate: (b: Record<string, unknown>) => unwrap<Record<string, unknown>>(http.post(`${SVC}/appointment-types`, b)),
  atUpdate: (id: string, b: Record<string, unknown>) => unwrap<Record<string, unknown>>(http.put(`${SVC}/appointment-types/${id}`, b)),
  atDelete: (id: string) => unwrap<{ deleted: string }>(http.delete(`${SVC}/appointment-types/${id}`)),
  areaList: () => unwrap<Record<string, unknown>[]>(http.get(`${SVC}/service-areas`)),
  areaCreate: (b: Record<string, unknown>) => unwrap<Record<string, unknown>>(http.post(`${SVC}/service-areas`, b)),
  areaDelete: (id: string) => unwrap<{ deleted: string }>(http.delete(`${SVC}/service-areas/${id}`)),
  intakeList: () => unwrap<Record<string, unknown>[]>(http.get(`${SVC}/intake-questions`)),
  intakeCreate: (b: Record<string, unknown>) => unwrap<Record<string, unknown>>(http.post(`${SVC}/intake-questions`, b)),
  intakeUpdate: (id: string, b: Record<string, unknown>) => unwrap<Record<string, unknown>>(http.put(`${SVC}/intake-questions/${id}`, b)),
  intakeDelete: (id: string) => unwrap<{ deleted: string }>(http.delete(`${SVC}/intake-questions/${id}`)),

  // Internal scheduling engine
  schedAvailability: (b: { appointment_type_id: string; date: string; service_id?: string | null; postal_code?: string | null; city?: string | null }) =>
    unwrap<AvailabilityResult>(http.post(`${SCHED}/availability`, b)),
  schedCreateAppointment: (b: Record<string, unknown>) => unwrap<Appointment>(http.post(`${SCHED}/appointments`, b)),
  schedListAppointments: (params: Record<string, string> = {}) => unwrap<Appointment[]>(http.get(`${SCHED}/appointments`, { params })),
  schedGetAppointment: (id: string) => unwrap<Appointment>(http.get(`${SCHED}/appointments/${id}`)),
  schedReschedule: (id: string, start_at: string) => unwrap<Appointment>(http.post(`${SCHED}/appointments/${id}/reschedule`, { start_at })),

  // Appointment reminders (owner) + tokenized public customer actions
  remindersConfig: () => unwrap<ReminderConfig>(http.get(`/api/v1/reminders/config`)),
  remindersConfigSet: (reminders: unknown[], ai_enabled = false) => unwrap<{ reminders: unknown[] }>(http.put(`/api/v1/reminders/config`, { reminders, ai_enabled })),
  remindersForAppointment: (apptId: string) => unwrap<{ reminder: ReminderDoc | null; customer_link: string | null }>(http.get(`/api/v1/reminders/appointment/${apptId}`)),
  publicApptGet: (t: string) => unwrap<PublicAppointment>(http.get(`/api/public/appointments/${t}`)),
  publicApptConfirm: (t: string) => unwrap<PublicAppointment>(http.post(`/api/public/appointments/${t}/confirm`)),
  publicApptCancel: (t: string) => unwrap<PublicAppointment>(http.post(`/api/public/appointments/${t}/cancel`)),
  publicApptAvailability: (t: string, date: string) => unwrap<{ slots: AvailabilitySlot[]; timezone: string }>(http.get(`/api/public/appointments/${t}/availability`, { params: { date } })),
  publicApptReschedule: (t: string, start_at: string) => unwrap<PublicAppointment>(http.post(`/api/public/appointments/${t}/reschedule`, { start_at })),

  // No-Show Recovery
  noshowConfig: () => unwrap<NoShowConfig>(http.get(`/api/v1/noshow/config`)),
  noshowMetrics: (date_filter = "last_30_days") => unwrap<NoShowMetrics>(http.get(`/api/v1/noshow/metrics`, { params: { date_filter } })),
  noshowCampaigns: () => unwrap<NoShowCampaign[]>(http.get(`/api/v1/noshow/campaigns`)),
  noshowMembers: () => unwrap<OrgMember[]>(http.get(`/api/v1/noshow/members`)),
  noshowAssign: (id: string, user_id: string | null) => unwrap<NoShowCampaign>(http.post(`/api/v1/noshow/campaigns/${id}/assign`, { user_id })),
  noshowStop: (id: string) => unwrap<{ stopped: string }>(http.post(`/api/v1/noshow/campaigns/${id}/stop`)),
  noshowProcess: () => unwrap<{ campaigns_scanned: number; details: unknown[] }>(http.post(`/api/v1/noshow/process`)),

  // Customer Reactivation Campaigns
  campaignsConfig: () => unwrap<CampaignConfig>(http.get(`/api/v1/campaigns/config`)),
  campaignsList: () => unwrap<Campaign[]>(http.get(`/api/v1/campaigns`)),
  campaignCreate: (b: Record<string, unknown>) => unwrap<Campaign>(http.post(`/api/v1/campaigns`, b)),
  campaignUpdate: (id: string, b: Record<string, unknown>) => unwrap<Campaign>(http.put(`/api/v1/campaigns/${id}`, b)),
  campaignAudiencePreview: (id: string) => unwrap<AudiencePreview>(http.post(`/api/v1/campaigns/${id}/audience/preview`)),
  campaignActivate: (id: string) => unwrap<Campaign>(http.post(`/api/v1/campaigns/${id}/activate`)),
  campaignSchedule: (id: string, scheduled_at: string) => unwrap<Campaign>(http.post(`/api/v1/campaigns/${id}/schedule`, { scheduled_at })),
  campaignPause: (id: string) => unwrap<Campaign>(http.post(`/api/v1/campaigns/${id}/pause`)),
  campaignProcess: (id: string) => unwrap<{ processed: number; stats: CampaignStats }>(http.post(`/api/v1/campaigns/${id}/process`)),

  // Outbound AI Calling
  outboundConfig: () => unwrap<OutboundConfig>(http.get(`/api/v1/outbound/config`)),
  outboundList: () => unwrap<OutboundCall[]>(http.get(`/api/v1/outbound`)),
  outboundEnqueue: (b: Record<string, unknown>) => unwrap<OutboundCall>(http.post(`/api/v1/outbound/enqueue`, b)),
  outboundCancel: (id: string) => unwrap<{ cancelled: string }>(http.post(`/api/v1/outbound/${id}/cancel`)),
  outboundProcess: () => unwrap<{ scanned: number; details: unknown[] }>(http.post(`/api/v1/outbound/process`)),

  // Automated Review Generation (owner) + tokenized public feedback
  reviewsConfig: () => unwrap<ReviewConfig>(http.get(`/api/v1/reviews/config`)),
  reviewsConfigSet: (b: { google_review_url?: string; message?: string; delay_hours?: number; internal_feedback_enabled?: boolean }) =>
    unwrap<Record<string, unknown>>(http.put(`/api/v1/reviews/config`, b)),
  reviewsList: () => unwrap<ReviewRequest[]>(http.get(`/api/v1/reviews`)),
  reviewsMetrics: (date_filter = "last_30_days") => unwrap<ReviewMetrics>(http.get(`/api/v1/reviews/metrics`, { params: { date_filter } })),
  reviewsProcess: () => unwrap<{ scanned: number; details: unknown[] }>(http.post(`/api/v1/reviews/process`)),
  publicReviewGet: (t: string) => unwrap<PublicReview>(http.get(`/api/public/reviews/${t}`)),
  publicReviewFeedback: (t: string, rating: number, comment: string) =>
    unwrap<{ received: boolean }>(http.post(`/api/public/reviews/${t}/feedback`, { rating, comment })),
  publicReviewClick: (t: string) => unwrap<{ google_review_url: string | null }>(http.post(`/api/public/reviews/${t}/click`)),

  // Customer Portal (owner link management + public tokenized view)
  portalConfig: () => unwrap<{ plan: string; included: boolean; estimates_supported: boolean; active_links: number }>(http.get(`/api/v1/portal/config`)),
  portalGetLink: (contactId: string) => unwrap<{ link: string | null; created_at?: string }>(http.get(`/api/v1/portal/contacts/${contactId}/link`)),
  portalCreateLink: (contactId: string) => unwrap<{ link: string }>(http.post(`/api/v1/portal/contacts/${contactId}/link`)),
  portalRevokeLink: (contactId: string) => unwrap<{ revoked: string }>(http.delete(`/api/v1/portal/contacts/${contactId}/link`)),
  publicPortalGet: (t: string) => unwrap<PublicPortal>(http.get(`/api/public/portal/${t}`)),

  // Revenue Attribution
  attribution: (params: Record<string, string>) => unwrap<AttributionData>(http.get(`/api/v1/attribution`, { params })),
  forecasts: (params?: Record<string, string>) => unwrap<ForecastData>(http.get(`/api/v1/forecasts`, { params: params || {} })),
  telephonyGoLive: () => unwrap<TelephonyGoLive>(http.get(`/api/v1/telephony/go-live`)),
  telephonyGoLiveUpdate: (body: { kyc_status?: string; a2p_status?: string }) =>
    unwrap<TelephonyGoLive>(http.put(`/api/v1/telephony/go-live`, body)),
  // Module 54 — Customer Telephony Provisioning
  telephonyMyProfile: () => unwrap<any>(http.get(`/api/v1/telephony/profile`)),
  telephonyAdminOverview: (params?: Record<string, string>) => unwrap<any>(http.get(`/api/internal/telephony/overview`, { params: params || {} })),
  telephonyAdminProfile: (orgId: string) => unwrap<any>(http.get(`/api/internal/telephony/orgs/${orgId}`)),
  telephonyProvisionStart: (orgId: string) => unwrap<any>(http.post(`/api/internal/telephony/orgs/${orgId}/provision/start`)),
  telephonyProvisionSubaccount: (orgId: string) => unwrap<any>(http.post(`/api/internal/telephony/orgs/${orgId}/provision/subaccount`)),
  telephonyProvisionRetry: (orgId: string) => unwrap<any>(http.post(`/api/internal/telephony/orgs/${orgId}/provision/retry`)),
  telephonyNumberSource: (orgId: string, source: string) => unwrap<any>(http.post(`/api/internal/telephony/orgs/${orgId}/number-source`, { source })),
  // Module 55 — Team + Business Availability
  teamMembers: (params?: Record<string, string>) => unwrap<{ members: any[] }>(http.get(`/api/v1/team/members`, { params: params || {} })),
  teamCreateMember: (b: Record<string, unknown>) => unwrap<any>(http.post(`/api/v1/team/members`, b)),
  teamGetMember: (id: string) => unwrap<any>(http.get(`/api/v1/team/members/${id}`)),
  teamUpdateMember: (id: string, b: Record<string, unknown>) => unwrap<any>(http.put(`/api/v1/team/members/${id}`, b)),
  teamDeleteMember: (id: string) => unwrap<{ deleted: string }>(http.delete(`/api/v1/team/members/${id}`)),
  teamGetAvailability: (id: string) => unwrap<{ windows: any[] }>(http.get(`/api/v1/team/members/${id}/availability`)),
  teamSetAvailability: (id: string, windows: any[]) => unwrap<{ windows: any[] }>(http.put(`/api/v1/team/members/${id}/availability`, windows)),
  teamGetTimeOff: (id: string) => unwrap<{ time_off: any[] }>(http.get(`/api/v1/team/members/${id}/time-off`)),
  teamAddTimeOff: (id: string, b: Record<string, unknown>) => unwrap<any>(http.post(`/api/v1/team/members/${id}/time-off`, b)),
  teamDeleteTimeOff: (id: string, offId: string) => unwrap<any>(http.delete(`/api/v1/team/members/${id}/time-off/${offId}`)),
  teamPlatformUsers: () => unwrap<{ users: any[] }>(http.get(`/api/v1/team/platform-users`)),
  availBusinessHours: () => unwrap<{ days: any[] }>(http.get(`/api/v1/availability/business-hours`)),
  availSetBusinessHours: (days: any[]) => unwrap<{ days: any[] }>(http.put(`/api/v1/availability/business-hours`, days)),
  availRules: () => unwrap<any>(http.get(`/api/v1/availability/rules`)),
  availSetRules: (patch: Record<string, unknown>) => unwrap<any>(http.put(`/api/v1/availability/rules`, patch)),
  availSpecialDates: () => unwrap<{ special_dates: any[] }>(http.get(`/api/v1/availability/special-dates`)),
  availAddSpecialDate: (b: Record<string, unknown>) => unwrap<any>(http.post(`/api/v1/availability/special-dates`, b)),
  availDeleteSpecialDate: (id: string) => unwrap<any>(http.delete(`/api/v1/availability/special-dates/${id}`)),
  optimizationMeta: () => unwrap<{ plan: string; entitlements: Record<string, boolean | string> }>(http.get(`/api/v1/optimization/meta`)),
  optimization: (params?: Record<string, string>) => unwrap<OptimizationData>(http.get(`/api/v1/optimization`, { params: params || {} })),
  optimizationReview: (key: string) => unwrap<{ key: string; status: string }>(http.post(`/api/v1/optimization/${key}/review`)),
  optimizationDismiss: (key: string) => unwrap<{ key: string; status: string }>(http.post(`/api/v1/optimization/${key}/dismiss`)),
  optimizationApply: (key: string) => unwrap<{ applied: boolean; knowledge_entry_id?: string; note?: string }>(http.post(`/api/v1/optimization/${key}/apply`)),
  qualityMeta: () => unwrap<QualityMeta>(http.get(`/api/v1/quality/meta`)),
  quality: (params: Record<string, string>) => unwrap<QualityData>(http.get(`/api/v1/quality`, { params })),
  qualityEvaluate: (body: { date_filter: string; custom_from?: string; custom_to?: string }) =>
    unwrap<{ available: boolean; evaluated?: number; remaining_unevaluated?: number; message?: string; reason?: string }>(http.post(`/api/v1/quality/evaluate`, body)),
  qualitySuggestionAccept: (id: string) =>
    unwrap<{ accepted: boolean; knowledge_entry_id: string; note: string }>(http.post(`/api/v1/quality/suggestions/${id}/accept`)),
  qualitySuggestionDismiss: (id: string) =>
    unwrap<{ dismissed: boolean }>(http.post(`/api/v1/quality/suggestions/${id}/dismiss`)),
  lostLeadsMeta: () => unwrap<LostLeadMeta>(http.get(`/api/v1/lost-leads/meta`)),
  lostLeads: (params: Record<string, string>) => unwrap<LostLeadData>(http.get(`/api/v1/lost-leads`, { params })),
  lostLeadsAnalyze: (body: { date_filter: string; custom_from?: string; custom_to?: string }) =>
    unwrap<{ available: boolean; classified?: number; remaining_unclassified?: number; message?: string; reason?: string }>(http.post(`/api/v1/lost-leads/analyze`, body)),
  lostLeadCorrect: (key: string, reason: string) =>
    unwrap<{ key: string; reason: string; reason_source: string }>(http.patch(`/api/v1/lost-leads/items/${encodeURIComponent(key)}`, { reason })),

  // Maintenance Membership Promotion
  membershipConfig: () => unwrap<MembershipConfig>(http.get(`/api/v1/memberships/config`)),
  membershipPlans: () => unwrap<MembershipPlan[]>(http.get(`/api/v1/memberships/plans`)),
  membershipPlanCreate: (b: Record<string, unknown>) => unwrap<MembershipPlan>(http.post(`/api/v1/memberships/plans`, b)),
  membershipPlanUpdate: (id: string, b: Record<string, unknown>) => unwrap<MembershipPlan>(http.put(`/api/v1/memberships/plans/${id}`, b)),
  membershipPlanDelete: (id: string) => unwrap<{ deactivated: string }>(http.delete(`/api/v1/memberships/plans/${id}`)),
  membershipEngagements: (stage?: string) => unwrap<MembershipEngagement[]>(http.get(`/api/v1/memberships/engagements`, { params: stage ? { stage } : {} })),
  membershipEngagementCreate: (b: Record<string, unknown>) => unwrap<{ recorded: boolean }>(http.post(`/api/v1/memberships/engagements`, b)),
  membershipStageChange: (id: string, stage: string, note?: string) => unwrap<MembershipEngagement>(http.put(`/api/v1/memberships/engagements/${id}/stage`, { stage, note })),
  membershipMetrics: (date_filter = "last_30_days") => unwrap<MembershipMetrics>(http.get(`/api/v1/memberships/metrics`, { params: { date_filter } })),

  // Seasonal Campaign Automation
  seasonalConfig: () => unwrap<SeasonalConfig>(http.get(`/api/v1/seasonal/config`)),
  seasonalTemplates: () => unwrap<SeasonalTemplate[]>(http.get(`/api/v1/seasonal/templates`)),
  seasonalTemplateCreate: (b: Record<string, unknown>) => unwrap<SeasonalTemplate>(http.post(`/api/v1/seasonal/templates`, b)),
  seasonalTemplateDelete: (id: string) => unwrap<{ deleted: string }>(http.delete(`/api/v1/seasonal/templates/${id}`)),
  seasonalRules: () => unwrap<SeasonalRule[]>(http.get(`/api/v1/seasonal/rules`)),
  seasonalRuleCreate: (b: Record<string, unknown>) => unwrap<SeasonalRule>(http.post(`/api/v1/seasonal/rules`, b)),
  seasonalRuleAuthorize: (id: string) => unwrap<SeasonalRule>(http.post(`/api/v1/seasonal/rules/${id}/authorize`)),
  seasonalRulePause: (id: string) => unwrap<SeasonalRule>(http.post(`/api/v1/seasonal/rules/${id}/pause`)),
  seasonalRuleResume: (id: string) => unwrap<SeasonalRule>(http.post(`/api/v1/seasonal/rules/${id}/resume`)),
  seasonalRuleCancel: (id: string) => unwrap<{ cancelled: string }>(http.delete(`/api/v1/seasonal/rules/${id}`)),
  seasonalCampaigns: () => unwrap<SeasonalOccurrence[]>(http.get(`/api/v1/seasonal/campaigns`)),
  seasonalCalendar: () => unwrap<SeasonalCalendar>(http.get(`/api/v1/seasonal/calendar`)),
  seasonalCampaignCreate: (b: Record<string, unknown>) => unwrap<SeasonalOccurrence>(http.post(`/api/v1/seasonal/campaigns`, b)),
  seasonalCampaignAuthorize: (id: string) => unwrap<SeasonalOccurrence>(http.post(`/api/v1/seasonal/campaigns/${id}/authorize`)),
  seasonalCampaignSend: (id: string) => unwrap<{ occurrence: string; sent: number; stats: SeasonalStats }>(http.post(`/api/v1/seasonal/campaigns/${id}/send`)),
  seasonalCampaignCancel: (id: string) => unwrap<{ cancelled: string }>(http.post(`/api/v1/seasonal/campaigns/${id}/cancel`)),
  seasonalMetrics: (date_filter = "last_90_days") => unwrap<SeasonalMetrics>(http.get(`/api/v1/seasonal/metrics`, { params: { date_filter } })),

  // Smart Dispatch Recommendations
  dispatchConfig: () => unwrap<DispatchConfig>(http.get(`/api/v1/dispatch/config`)),
  dispatchConfigSet: (auto_assign: boolean) => unwrap<DispatchConfig>(http.put(`/api/v1/dispatch/config`, { auto_assign })),
  dispatchTechnicians: () => unwrap<DispatchTechnician[]>(http.get(`/api/v1/dispatch/technicians`)),
  dispatchTechnicianSet: (userId: string, b: Record<string, unknown>) => unwrap<Record<string, unknown>>(http.put(`/api/v1/dispatch/technicians/${userId}`, b)),
  dispatchRecommendations: (apptId: string) => unwrap<DispatchRecommendations>(http.get(`/api/v1/dispatch/appointments/${apptId}/recommendations`)),
  dispatchAssign: (apptId: string, technician_id: string) => unwrap<Appointment>(http.post(`/api/v1/dispatch/appointments/${apptId}/assign`, { technician_id })),
  dispatchUnassign: (apptId: string) => unwrap<Appointment>(http.post(`/api/v1/dispatch/appointments/${apptId}/unassign`)),

  // Technician ETA & On-the-Way Notifications
  etaConfig: () => unwrap<EtaConfig>(http.get(`/api/v1/eta/config`)),
  etaConfigSet: (b: { notify?: Record<string, boolean>; messages?: Record<string, string> }) => unwrap<EtaConfig>(http.put(`/api/v1/eta/config`, b)),
  etaSetStatus: (apptId: string, b: { job_status: string; eta_window_start?: string | null; eta_window_end?: string | null }) =>
    unwrap<{ appointment: Appointment; notification: EtaNotification | null }>(http.post(`/api/v1/eta/appointments/${apptId}/status`, b)),

  // Contractor Integration Framework
  ciCatalog: () => unwrap<IntegrationCatalog>(http.get(`/api/v1/contractor-integrations/catalog`)),
  ciConnect: (provider: string, credentials: Record<string, string>) =>
    unwrap<{ provider: string; status: string; detail: string; last_verified_at: string | null }>(http.post(`/api/v1/contractor-integrations/${provider}/connect`, { credentials })),
  ciVerify: (provider: string) => unwrap<{ provider: string; status: string; detail: string }>(http.post(`/api/v1/contractor-integrations/${provider}/verify`)),
  ciDisconnect: (provider: string) => unwrap<{ disconnected: string }>(http.delete(`/api/v1/contractor-integrations/${provider}`)),

  // Multi-Location Routing
  locConfig: () => unwrap<LocationConfig>(http.get(`/api/v1/locations/config`)),
  locList: () => unwrap<BranchLocation[]>(http.get(`/api/v1/locations`)),
  locCreate: (b: Record<string, unknown>) => unwrap<BranchLocation>(http.post(`/api/v1/locations`, b)),
  locUpdate: (id: string, b: Record<string, unknown>) => unwrap<BranchLocation>(http.put(`/api/v1/locations/${id}`, b)),
  locDelete: (id: string) => unwrap<{ deleted: string }>(http.delete(`/api/v1/locations/${id}`)),
  locRoute: (b: { zip?: string; service?: string; phone_dialed?: string }) => unwrap<RouteResult>(http.post(`/api/v1/locations/route`, b)),
  schedCancel: (id: string, reason?: string) => unwrap<Appointment>(http.post(`${SCHED}/appointments/${id}/cancel`, { reason })),
  schedConfirm: (id: string) => unwrap<Appointment>(http.post(`${SCHED}/appointments/${id}/confirm`)),
  schedComplete: (id: string) => unwrap<Appointment>(http.post(`${SCHED}/appointments/${id}/complete`)),
  schedNoShow: (id: string) => unwrap<Appointment>(http.post(`${SCHED}/appointments/${id}/no-show`)),
  schedBlockedList: () => unwrap<BlockedTime[]>(http.get(`${SCHED}/blocked-times`)),
  schedBlockedCreate: (b: { start_at: string; end_at: string; reason?: string | null }) => unwrap<BlockedTime>(http.post(`${SCHED}/blocked-times`, b)),
  schedBlockedDelete: (id: string) => unwrap<{ deleted: string }>(http.delete(`${SCHED}/blocked-times/${id}`)),

  // Integrations — Google Calendar
  gcalStatus: () => unwrap<CalendarConnection>(http.get(`/api/v1/integrations/google/status`)),
  gcalConnect: () => unwrap<{ authorization_url: string }>(http.get(`/api/v1/integrations/google/connect`)),
  gcalCalendars: () => unwrap<{ calendars: GoogleCalendarItem[] }>(http.get(`/api/v1/integrations/google/calendars`)),
  gcalChoose: (calendar_id: string) => unwrap<{ calendar: GoogleCalendarItem }>(http.put(`/api/v1/integrations/google/calendar`, { calendar_id })),
  gcalTest: () => unwrap<{ ok: boolean; calendar_count?: number; chosen_calendar?: string; chosen_calendar_present?: boolean; writable?: boolean; error?: string }>(http.post(`/api/v1/integrations/google/test`)),
  gcalDisconnect: () => unwrap<{ disconnected: boolean }>(http.post(`/api/v1/integrations/google/disconnect`)),
  gcalRetrySync: () => unwrap<{ attempted: number; synced: number; still_failed: number }>(http.post(`/api/v1/integrations/google/retry-sync`)),
  gcalLogs: () => unwrap<{ logs: CalendarSyncLog[] }>(http.get(`/api/v1/integrations/google/logs`)),

  inboxMeta: () => unwrap<{ statuses: { value: string; label: string }[]; channels: { value: string; label: string }[] }>(http.get(`/api/v1/inbox/meta`)),
  inboxList: (params: Record<string, string> = {}) => unwrap<InboxConversation[]>(http.get(`/api/v1/inbox/conversations`, { params })),
  inboxCounts: (params: Record<string, string> = {}) => unwrap<{ total: number; calls: number; texts: number }>(http.get(`/api/v1/inbox/counts`, { params })),
  inboxGet: (id: string) => unwrap<InboxDetail>(http.get(`/api/v1/inbox/conversations/${id}`)),
  inboxRead: (id: string) => unwrap<{ read: string }>(http.post(`/api/v1/inbox/conversations/${id}/read`)),
  inboxReply: (id: string, body: string) => unwrap<InboxMessage>(http.post(`/api/v1/inbox/conversations/${id}/reply`, { body })),
  inboxNote: (id: string, body: string) => unwrap<InboxMessage>(http.post(`/api/v1/inbox/conversations/${id}/note`, { body })),
  inboxSimulate: (id: string, body: string, auto_ai_reply = true) => unwrap<Record<string, unknown>>(http.post(`/api/v1/inbox/conversations/${id}/simulate`, { body, auto_ai_reply })),
  inboxStatus: (id: string, status: string) => unwrap<InboxConversation>(http.post(`/api/v1/inbox/conversations/${id}/status`, { status })),
  inboxResolve: (id: string) => unwrap<InboxConversation>(http.post(`/api/v1/inbox/conversations/${id}/resolve`)),
  inboxSpam: (id: string) => unwrap<InboxConversation>(http.post(`/api/v1/inbox/conversations/${id}/spam`)),
  inboxEscalate: (id: string, reason?: string) => unwrap<InboxConversation>(http.post(`/api/v1/inbox/conversations/${id}/escalate`, { reason })),
  inboxAiToggle: (id: string, ai_active: boolean) => unwrap<InboxConversation>(http.post(`/api/v1/inbox/conversations/${id}/ai`, { ai_active })),
  inboxAssign: (id: string, assigned_to: string | null) => unwrap<InboxConversation>(http.post(`/api/v1/inbox/conversations/${id}/assign`, { assigned_to })),
  inboxTag: (id: string, tag_id: string) => unwrap<Record<string, unknown>[]>(http.post(`/api/v1/inbox/conversations/${id}/tags`, { tag_id })),
  inboxUntag: (id: string, tag_id: string) => unwrap<Record<string, unknown>[]>(http.delete(`/api/v1/inbox/conversations/${id}/tags/${tag_id}`)),
  inboxSeed: () => unwrap<{ created: number; conversation_ids: string[] }>(http.post(`/api/v1/inbox/seed-samples`)),

  // AI Receptionist
  receptionistMeta: () => unwrap<{ intents: string[]; model: string; configured: boolean; confidence_threshold: number; low_confidence_message: string }>(http.get(`/api/v1/receptionist/meta`)),
  receptionistSimulate: (body: { conversation_id?: string; text: string; contact?: { first_name?: string; last_name?: string; phone?: string } }) => unwrap<ReceptionistTurn>(http.post(`/api/v1/receptionist/simulate`, body)),
  receptionistInteractions: (conversation_id: string) => unwrap<AiInteraction[]>(http.get(`/api/v1/receptionist/interactions`, { params: { conversation_id } })),

  // AI Receptionist Simulator (isolated sandbox)
  simMeta: () => unwrap<{ scenarios: SimScenario[]; configs: { id: string; name: string }[]; model: string; configured: boolean }>(http.get(`/api/v1/simulator/meta`)),
  simSessions: () => unwrap<(SimSession & { turn_count: number })[]>(http.get(`/api/v1/simulator/sessions`)),
  simCreate: (scenario_key: string, config_id = "default") => unwrap<SimSession>(http.post(`/api/v1/simulator/sessions`, { scenario_key, config_id })),
  simGet: (sid: string) => unwrap<SimSession>(http.get(`/api/v1/simulator/sessions/${sid}`)),
  simMessage: (sid: string, text: string) => unwrap<SimSession>(http.post(`/api/v1/simulator/sessions/${sid}/message`, { text })),
  simDecide: (sid: string, actionId: string, decision: "approve" | "reject") => unwrap<SimSession>(http.post(`/api/v1/simulator/sessions/${sid}/actions/${actionId}`, { decision })),
  simChecklist: (sid: string, item_key: string, status: string) => unwrap<SimSession>(http.post(`/api/v1/simulator/sessions/${sid}/checklist`, { item_key, status })),
  simReset: (sid: string) => unwrap<SimSession>(http.post(`/api/v1/simulator/sessions/${sid}/reset`)),

  // AI-assisted SMS
  smsMeta: () => unwrap<{ modes: string[]; model: string; configured: boolean }>(http.get(`/api/v1/sms/meta`)),
  smsSettings: () => unwrap<any>(http.get(`/api/v1/sms/settings`)),
  smsUpdateSettings: (patch: any) => unwrap<any>(http.put(`/api/v1/sms/settings`, patch)),
  smsInbound: (b: { from_number: string; to_number?: string; body: string; message_sid?: string; fail_send?: boolean }) => unwrap<any>(http.post(`/api/v1/sms/inbound`, b)),
  smsDrafts: () => unwrap<any[]>(http.get(`/api/v1/sms/drafts`)),
  smsSendDraft: (id: string, fail_send = false) => unwrap<any>(http.post(`/api/v1/sms/drafts/${id}/send`, { fail_send })),
  smsDiscardDraft: (id: string) => unwrap<any>(http.post(`/api/v1/sms/drafts/${id}/discard`)),

  // Voice call orchestration
  voiceMeta: () => unwrap<{ outcomes: string[]; provider: string; provider_configured: boolean; telephony_configured: boolean; llm_configured: boolean; model: string }>(http.get(`/api/v1/voice/meta`)),
  voiceSettings: () => unwrap<any>(http.get(`/api/v1/voice/settings`)),
  voiceUpdateSettings: (patch: any) => unwrap<any>(http.put(`/api/v1/voice/settings`, patch)),
  voiceVerifyNumber: (number: string) => unwrap<any>(http.post(`/api/v1/voice/test-call/verify`, { number })),
  voiceConfirmNumber: (number: string, code: string) => unwrap<any>(http.post(`/api/v1/voice/test-call/confirm`, { number, code })),
  voiceStartTestCall: (number: string) => unwrap<any>(http.post(`/api/v1/voice/test-call/start`, { number })),
  voiceReadiness: () => unwrap<{ checks: { key: string; label: string; passed: boolean; detail: string }[]; ready: boolean; number_active: boolean }>(http.get(`/api/v1/voice/readiness`)),
  voiceActivate: () => unwrap<any>(http.post(`/api/v1/voice/activate`)),
  voiceDeactivate: () => unwrap<any>(http.post(`/api/v1/voice/deactivate`)),
  voiceSimStart: (b: { from_number: string; to_number?: string; caller_name?: string }) => unwrap<any>(http.post(`/api/v1/voice/simulate/start`, b)),
  voiceSimTurn: (b: { call_record_id: string; conversation_id: string; text: string }) => unwrap<any>(http.post(`/api/v1/voice/simulate/turn`, b)),
  voiceSimEnd: (b: { call_record_id: string; disconnect_reason?: string }) => unwrap<any>(http.post(`/api/v1/voice/simulate/end`, b)),
  voiceGetCall: (id: string) => unwrap<{ call: any; segments: any[] }>(http.get(`/api/v1/voice/calls/${id}`)),

  // AI Receptionist Management Center
  rcCenter: () => unwrap<any>(http.get(`/api/v1/receptionist/center`)),
  rcIdentity: (patch: any) => unwrap<any>(http.put(`/api/v1/receptionist/identity`, patch)),
  rcCapabilities: (patch: any) => unwrap<any>(http.put(`/api/v1/receptionist/capabilities`, patch)),
  rcEscalation: (patch: any) => unwrap<any>(http.put(`/api/v1/receptionist/escalation`, patch)),
  rcAfterHours: (patch: any) => unwrap<any>(http.put(`/api/v1/receptionist/after-hours`, patch)),
  rcGuardrails: (patch: any) => unwrap<any>(http.put(`/api/v1/receptionist/guardrails`, patch)),
  rcQualification: () => unwrap<{ groups: { service_id: string; service_name: string; questions: any[] }[]; total_questions: number }>(http.get(`/api/v1/receptionist/qualification`)),
  rcReadiness: () => unwrap<{ checks: { key: string; label: string; passed: boolean; detail: string }[]; ready: boolean; status: string; tests: any }>(http.get(`/api/v1/receptionist/readiness`)),
  rcSetStatus: (status: string) => unwrap<any>(http.post(`/api/v1/receptionist/status`, { status })),
  rcActivity: () => unwrap<any>(http.get(`/api/v1/receptionist/activity`)),

  // Internal admin portal (Phase 1)
  internalMe: () => unwrap<{ id: string; email: string; role: string; title: string | null; status: string; is_platform_admin: boolean; is_senior_admin: boolean }>(http.get(`/api/internal/me`)),
  internalResetMfa: (email: string, reason: string) =>
    unwrap<{ reset: boolean; email: string; message: string }>(http.post(`/api/internal/users/mfa/reset`, { email, reason })),
  internalOverview: () => unwrap<any>(http.get(`/api/internal/overview`)),
  internalAuditLogs: (params: Record<string, string>) => unwrap<{ total: number; logs: any[] }>(http.get(`/api/internal/audit-logs`, { params })),
  internalBootstrapStatus: () => unwrap<{ available: boolean }>(http.get(`/api/internal/bootstrap-status`)),
  internalActivate: (body: { token: string; password: string; first_name?: string; last_name?: string }) => unwrap<{ activated: boolean; email: string }>(http.post(`/api/internal/activate`, body)),

  // Customer Onboarding Secure Payment Flow (Phase 1)
  onboardingCreate: (body: Record<string, unknown>) => unwrap<{ account: any; id: string; organization_id: string; activation_path: string }>(http.post(`/api/internal/onboarding/accounts`, body)),
  onboardingList: () => unwrap<{ accounts: any[] }>(http.get(`/api/internal/onboarding/accounts`)),
  onboardingResend: (id: string) => unwrap<{ account: any; id: string; activation_path: string }>(http.post(`/api/internal/onboarding/accounts/${id}/resend`)),
  onboardingRevoke: (id: string) => unwrap<{ account: any; id: string }>(http.post(`/api/internal/onboarding/accounts/${id}/revoke`)),
  onboardingDisclosure: (id: string) => unwrap<{ interval: string; version: number; text: string; acknowledged: boolean; acknowledgement: any }>(http.get(`/api/internal/onboarding/accounts/${id}/disclosure`)),
  onboardingAcknowledge: (id: string) => unwrap<{ acknowledgement: any }>(http.post(`/api/internal/onboarding/accounts/${id}/disclosure`)),
  disclosuresList: () => unwrap<{ disclosures: { id: string; interval: string; version: number; text: string }[] }>(http.get(`/api/internal/onboarding/disclosures`)),
  disclosureUpdate: (interval: string, text: string) => unwrap<{ disclosure: any }>(http.put(`/api/internal/onboarding/disclosures/${interval}`, { text })),
  activationGet: (token: string) => unwrap<any>(http.get(`/api/public/activation/${token}`)),
  activationCheckout: (token: string) => unwrap<{ checkout_url: string; session_id: string }>(http.post(`/api/public/activation/${token}/checkout`)),
  activationStatus: (token: string) => unwrap<{ paid: boolean; status: string }>(http.get(`/api/public/activation/${token}/payment-status`)),
  activationSetPassword: (token: string, body: { password: string; first_name?: string; last_name?: string }) => unwrap<{ token: string; organization_id: string; email: string; redirect_to: string }>(http.post(`/api/public/activation/${token}/set-password`, body)),

  // Internal Sales Compensation (Module 39) — role-gated server-side
  salesDashboard: (as_of?: string) => unwrap<any>(http.get(`/api/internal/sales/dashboard`, { params: as_of ? { as_of } : {} })),
  salesGrowthMix: (as_of?: string) => unwrap<any>(http.get(`/api/internal/sales/growth-mix`, { params: as_of ? { as_of } : {} })),
  salesReps: (as_of?: string) => unwrap<{ reps: any[]; totals: any }>(http.get(`/api/internal/sales/reps`, { params: as_of ? { as_of } : {} })),
  salesRepDetail: (userId: string, period_index?: number) => unwrap<{ summary: any; sales: any[] }>(http.get(`/api/internal/sales/reps/${userId}`, { params: period_index != null ? { period_index: String(period_index) } : {} })),
  salesMe: () => unwrap<any>(http.get(`/api/internal/sales/me`)),
  salesMyActivity: () => unwrap<{ windows: any[] }>(http.get(`/api/internal/sales/me/activity`)),
  salesMyNotifications: () => unwrap<{ notifications: any[] }>(http.get(`/api/internal/sales/me/notifications`)),
  salesLedger: (params: Record<string, string>) => unwrap<{ total: number; sales: any[] }>(http.get(`/api/internal/sales/ledger`, { params })),
  salesSaleDetail: (saleId: string) => unwrap<any>(http.get(`/api/internal/sales/ledger/${saleId}`)),
  salesCorrectAttribution: (saleId: string, body: { new_rep_user_id: string; reason: string }) => unwrap<any>(http.post(`/api/internal/sales/ledger/${saleId}/attribution`, body)),
  salesCreateAdjustment: (body: { user_id: string; amount: number; reason: string; period_index?: number; type?: string }) => unwrap<any>(http.post(`/api/internal/sales/adjustments`, body)),
  salesTeam: () => unwrap<{ reps: any[] }>(http.get(`/api/internal/sales/team`)),
  salesUpdateRep: (userId: string, body: Record<string, any>) => unwrap<any>(http.put(`/api/internal/sales/team/${userId}`, body)),
  salesPayouts: () => unwrap<{ batches: any[] }>(http.get(`/api/internal/sales/payouts`)),
  salesCreatePayout: (body: { as_of?: string; label?: string }) => unwrap<any>(http.post(`/api/internal/sales/payouts`, body)),
  salesPayoutDetail: (batchId: string) => unwrap<any>(http.get(`/api/internal/sales/payouts/${batchId}`)),
  salesSetPayoutStatus: (batchId: string, status: string) => unwrap<any>(http.post(`/api/internal/sales/payouts/${batchId}/status`, {}, { params: { status } })),
  salesMarkPayoutPaid: (batchId: string, body: { payment_date: string; method: string; reference?: string; notes?: string }) => unwrap<any>(http.post(`/api/internal/sales/payouts/${batchId}/mark-paid`, body)),
  salesPayoutExportUrl: (batchId: string) => `${API_BASE}/api/internal/sales/payouts/${batchId}/export.csv`,
  salesStatement: (userId: string, period_index?: number) => unwrap<{ statement: any; sales: any[] }>(http.get(`/api/internal/sales/statement/${userId}`, { params: period_index != null ? { period_index: String(period_index) } : {} })),
  salesCheckoutLink: (plan: string) => unwrap<{ url: string; ref_token: string; plan: string }>(http.post(`/api/internal/sales/checkout-link`, {}, { params: { plan } })),
  salesConnectStatus: (userId?: string) => unwrap<any>(http.get(`/api/internal/sales/payouts/connect/status`, { params: userId ? { user_id: userId } : {} })),
  salesConnectOnboard: () => unwrap<{ url: string; account_id: string }>(http.post(`/api/internal/sales/payouts/connect/onboard`, {})),
  salesPayRep: (batchId: string, userId: string) => unwrap<any>(http.post(`/api/internal/sales/payouts/${batchId}/pay/${userId}`, {})),

  // Executive Command Center (senior_access_admin only)
  execOverview: () => unwrap<any>(http.get(`/api/executive/overview`)),
  execBusinesses: (params: Record<string, string>) => unwrap<{ total: number; businesses: any[] }>(http.get(`/api/executive/businesses`, { params })),
  execIndustry: () => unwrap<{ industries: any[] }>(http.get(`/api/executive/industry`)),
  execSubscriptionMix: () => unwrap<any>(http.get(`/api/executive/subscription-mix`)),
  execGeo: () => unwrap<{ by_state: any[]; by_city: any[] }>(http.get(`/api/executive/geo`)),
  execSalesTeam: () => unwrap<any>(http.get(`/api/executive/sales-team`)),
  execRepDetail: (userId: string) => unwrap<any>(http.get(`/api/executive/reps/${userId}`)),
  execCompensationLiability: () => unwrap<any>(http.get(`/api/executive/compensation-liability`)),
  execBonuses: () => unwrap<any>(http.get(`/api/executive/bonuses`)),
  execAlerts: () => unwrap<{ alerts: any[] }>(http.get(`/api/executive/alerts`)),
  execReconciliation: () => unwrap<any>(http.get(`/api/executive/reconciliation`)),
  execSearch: (q: string) => unwrap<any>(http.get(`/api/executive/search`, { params: { q } })),
  execExportUrl: (kind: string) => `${API_BASE}/api/executive/export/${kind}.csv`,
  seniorBootstrapStatus: () => unwrap<{ available: boolean }>(http.get(`/api/internal/senior/bootstrap-status`)),

  // Multilingual (Phase 1)
  langCatalog: () => unwrap<any>(http.get(`/api/languages/catalog`)),
  langSettings: () => unwrap<any>(http.get(`/api/languages/settings`)),
  langUpdateSettings: (body: Record<string, any>) => unwrap<any>(http.put(`/api/languages/settings`, body)),
  langDetect: (text: string) => unwrap<any>(http.post(`/api/languages/detect`, { text })),
  langTranslate: (text: string, target: string, source?: string) => unwrap<any>(http.post(`/api/languages/translate`, { text, target, source })),
  langAnalytics: () => unwrap<any>(http.get(`/api/languages/analytics`)),

  // Transactional notifications
  ntfMeta: () => unwrap<{ types: string[]; customer_types: string[]; staff_types: string[]; statuses: string[]; audience: Record<string, string>; email_configured: boolean; sms_simulated: boolean }>(http.get(`/api/v1/notifications/meta`)),
  ntfSettings: () => unwrap<any>(http.get(`/api/v1/notifications/settings`)),
  ntfUpdateSettings: (patch: any) => unwrap<any>(http.put(`/api/v1/notifications/settings`, patch)),
  ntfTemplates: () => unwrap<any[]>(http.get(`/api/v1/notifications/templates`)),
  ntfList: (status?: string) => unwrap<any[]>(http.get(`/api/v1/notifications${status ? `?status=${status}` : ""}`)),
  ntfStats: () => unwrap<Record<string, number>>(http.get(`/api/v1/notifications/stats`)),
  ntfTest: (b: { type: string; channel: string; recipient: string }) => unwrap<any>(http.post(`/api/v1/notifications/test`, b)),
  ntfProcessDue: () => unwrap<any>(http.post(`/api/v1/notifications/process-due`)),

  // Billing / plans / usage (demo billing in V1)
  publicPlans: () => unwrap<any[]>(http.get(`/api/plans`)),
  billingSubscription: () => unwrap<any>(http.get(`/api/v1/billing/subscription`)),
  billingUsage: () => unwrap<any>(http.get(`/api/v1/billing/usage`)),
  billingSelectPlan: (plan_type: string) => unwrap<any>(http.post(`/api/v1/billing/select-plan`, { plan_type })),
  billingCancel: () => unwrap<any>(http.post(`/api/v1/billing/cancel`)),
  billingResume: () => unwrap<any>(http.post(`/api/v1/billing/resume`)),
  billingContactSales: (b: { message?: string; name?: string; email?: string; phone?: string }) => unwrap<any>(http.post(`/api/v1/billing/contact-sales`, b)),
  billingPortal: () => unwrap<{ demo: boolean; message: string }>(http.post(`/api/v1/billing/portal`)),
  billingStripeStatus: () => unwrap<{ configured: boolean; tax_mode: string }>(http.get(`/api/v1/billing/stripe-status`)),
  billingCheckout: (plan_type: string) => unwrap<{ checkout_url: string; session_id: string }>(http.post(`/api/v1/billing/checkout`, { plan_type })),
  billingCheckoutStatus: (session_id: string) => unwrap<{ paid: boolean; payment_status: string; status: string }>(http.get(`/api/v1/billing/checkout/status/${session_id}`)),
  publicCheckout: (body: {
    first_name: string; last_name: string; business_name: string; email: string; phone: string;
    industry: string; city: string; state: string; plan_type: string; referral_code?: string; rep?: string;
    sms_consent?: boolean; sms_marketing_consent?: boolean;
  }) => unwrap<{ checkout_url: string; session_id: string; prospect_id: string }>(http.post(`/api/public/checkout`, body)),
  publicCheckoutStatus: (session_id: string) => unwrap<{ paid: boolean; payment_status: string; status: string; organization_id: string | null; prospect: { business_name?: string; plan_type?: string } | null }>(http.get(`/api/public/checkout/status/${session_id}`)),
  partnerInquiry: (body: {
    first_name: string; last_name: string; agency_name: string; email: string; phone: string;
    website?: string; client_count?: string; industries?: string; message?: string;
    sms_consent?: boolean;
  }) => unwrap<{ received: boolean; inquiry_id: string }>(http.post(`/api/public/partner-inquiry`, body)),
  validateCode: (code: string) => unwrap<{
    valid: boolean; reason?: string; code?: string; code_type?: "partner_referral" | "discount";
    partner_name?: string | null; discount_type?: string | null;
    setup_discount_percent?: number | null; subscription_discount_percent?: number | null;
    discount_duration?: string | null;
  }>(http.get(`/api/public/validate-code?code=${encodeURIComponent(code)}`)),
  agencySummary: () => unwrap<any>(http.get(`/api/v1/admin/agency/summary`)),
  agencyReferrals: () => unwrap<any[]>(http.get(`/api/v1/admin/agency/referrals`)),
  agencyPartners: () => unwrap<any[]>(http.get(`/api/v1/admin/agency/partners`)),
  chatConfig: () => unwrap<{ configured: boolean }>(http.get(`/api/chat/config`)),
  chatStart: (organization_id?: string) => unwrap<{ chat_id: string; business_name: string }>(http.post(`/api/chat/start`, { organization_id })),
  chatMessage: (chat_id: string, content: string) => unwrap<{ messages: { role: string; content: string }[] }>(http.post(`/api/chat/message`, { chat_id, content })),
  // Sent while the page unloads, so it uses fetch with keepalive rather than axios.
  chatEnd: (chat_id: string) => {
    void fetch(`${API_BASE}/api/chat/end`, {
      method: "POST", keepalive: true, credentials: "include",
      headers: { "Content-Type": "application/json" }, body: JSON.stringify({ chat_id }),
    }).catch(() => undefined);
  },
  billingPortalSession: () => unwrap<{ portal_url: string }>(http.post(`/api/v1/billing/portal-session`)),
  billingAdminPlans: () => unwrap<any[]>(http.get(`/api/v1/billing/admin/plans`)),
  billingAdminUpdatePlan: (plan_type: string, patch: any) => unwrap<any>(http.put(`/api/v1/billing/admin/plans/${plan_type}`, { patch })),
  billingAdminSetupFee: (org_id: string, status: string, amount?: number) => unwrap<any>(http.put(`/api/v1/billing/admin/org/${org_id}/setup-fee`, { status, amount })),
  billingAdminOverrides: (org_id: string, overrides: any) => unwrap<any>(http.put(`/api/v1/billing/admin/org/${org_id}/entitlements`, { overrides })),
  billingAdminConvertCustom: (org_id: string, overrides: any) => unwrap<any>(http.post(`/api/v1/billing/admin/org/${org_id}/convert-custom`, { overrides })),

  // Retell conversational voice (web call)
  voiceRetellWebCall: () => unwrap<{ access_token: string; call_id: string; conversation_id: string; call_record_id: string }>(http.post(`/api/v1/voice/retell/web-call`)),
  voiceCompleteWebCall: (id: string, transcript: { role: string; content: string }[], duration_seconds?: number) =>
    unwrap<{ status: string; outcome: string | null; summary: string | null }>(http.post(`/api/v1/voice/retell/web-call/${id}/complete`, { transcript, duration_seconds })),
  voiceRetellConfigUrls: () => unwrap<any>(http.get(`/api/v1/voice/retell/config-urls`)),

  // Analytics (org-isolated; owner + staff)
  analyticsMeta: () => unwrap<{ date_filters: string[]; export_kinds: string[] }>(http.get(`/api/v1/analytics/meta`)),
  opportunity: (params: { date_filter: string; custom_from?: string; custom_to?: string }) =>
    unwrap<any>(http.get(`/api/v1/analytics/opportunity`, { params })),
  opportunityServiceValues: () => unwrap<any[]>(http.get(`/api/v1/analytics/opportunity/service-values`)),
  opportunityServiceValueSave: (body: { service_name: string; average_value: number; kind?: string }) =>
    unwrap<any[]>(http.put(`/api/v1/analytics/opportunity/service-values`, body)),
  analyticsOverview: (params: { date_filter: string; custom_from?: string; custom_to?: string }) =>
    unwrap<any>(http.get(`/api/v1/analytics/overview`, { params })),
  analyticsExport: async (kind: string, params: { date_filter: string; custom_from?: string; custom_to?: string }) => {
    const res = await http.get(`/api/v1/analytics/export`, { params: { kind, ...params }, responseType: "blob" });
    const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = `${kind}_${params.date_filter}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  },
  analyticsDigestGet: () => unwrap<{ enabled: boolean; last_sent_at: string | null; email_configured: boolean }>(http.get(`/api/v1/analytics/digest`)),
  analyticsDigestSet: (enabled: boolean) => unwrap<any>(http.put(`/api/v1/analytics/digest`, { enabled })),
  analyticsDigestSendNow: () => unwrap<{ sent: boolean; to?: string; simulated?: boolean; reason?: string }>(http.post(`/api/v1/analytics/digest/send-now`)),

  getOnboarding: () => unwrap<OnboardingState>(http.get(ONB)),
  saveBusinessProfile: (b: Record<string, unknown>) => unwrap<OnboardingState>(http.put(`${ONB}/business-profile`, b)),
  saveLocation: (b: Record<string, unknown>) => unwrap<OnboardingState>(http.put(`${ONB}/location`, b)),
  saveHours: (b: Record<string, unknown>) => unwrap<OnboardingState>(http.put(`${ONB}/hours`, b)),
  addService: (b: Record<string, unknown>) => unwrap<Record<string, unknown>>(http.post(`${ONB}/services`, b)),
  deleteService: (id: string) => unwrap<{ deleted: string }>(http.delete(`${ONB}/services/${id}`)),
  saveReceptionist: (b: Record<string, unknown>) => unwrap<OnboardingState>(http.put(`${ONB}/receptionist`, b)),
  addFaq: (b: Record<string, unknown>) => unwrap<Record<string, unknown>>(http.post(`${ONB}/faqs`, b)),
  deleteFaq: (id: string) => unwrap<{ deleted: string }>(http.delete(`${ONB}/faqs/${id}`)),
  getReadiness: () => unwrap<ReadinessState>(http.get(`${ONB}/readiness`)),
  completeOnboarding: () => unwrap<{ onboarding_status: string }>(http.post(`${ONB}/complete`)),

  register: (payload: {
    email: string; password: string; first_name: string; last_name: string;
    organization_name: string; industry: string;
  }) => unwrap<AuthResult & { redirect_to: string; onboarding_required: boolean }>(http.post("/api/auth/register", payload)),
  login: (email: string, password: string) => unwrap<AuthResult>(http.post("/api/auth/login", { email, password })),
  loginMfa: (challenge_token: string, code: string) =>
    unwrap<AuthResult & { backup_code_used?: boolean }>(http.post("/api/auth/login/mfa", { challenge_token, code })),
  logout: () => unwrap<{ logged_out: boolean }>(http.post("/api/auth/logout")),
  me: () => unwrap<AuthMe>(http.get("/api/auth/me")),
  changePassword: (current_password: string, new_password: string) =>
    unwrap<{ changed: boolean; reauth_required: boolean }>(http.post("/api/auth/change-password", { current_password, new_password })),
  mfaStatus: () => unwrap<MfaStatus>(http.get("/api/v1/mfa/status")),
  mfaSetup: () => unwrap<{ secret: string; otpauth_uri: string }>(http.post("/api/v1/mfa/setup")),
  mfaEnable: (code: string) => unwrap<{ enabled: boolean; backup_codes: string[] }>(http.post("/api/v1/mfa/enable", { code })),
  mfaDisable: (code: string) => unwrap<{ enabled: boolean }>(http.post("/api/v1/mfa/disable", { code })),
  mfaRegenerateBackupCodes: (code: string) => unwrap<{ backup_codes: string[] }>(http.post("/api/v1/mfa/backup-codes/regenerate", { code })),
  verifyEmail: (token: string) => unwrap<{ verified: boolean }>(http.post("/api/auth/verify-email", { token })),
  resendVerification: () => unwrap<{ sent?: boolean; already_verified?: boolean }>(http.post("/api/auth/resend-verification")),
  forgotPassword: (email: string) => unwrap<{ message: string }>(http.post("/api/auth/forgot-password", { email })),
  resetPassword: (token: string, password: string) => unwrap<{ message: string }>(http.post("/api/auth/reset-password", { token, password })),

  getProfile: () => unwrap<Profile>(http.get("/api/v1/profile")),
  updateProfile: (b: Partial<Profile>) => unwrap<Profile>(http.put("/api/v1/profile", b)),
  getOrganization: () => unwrap<{ organization: OrganizationFull; role: string }>(http.get("/api/v1/organizations/current")),
  updateOrganization: (b: Partial<OrganizationFull>) => unwrap<{ organization: OrganizationFull; role: string }>(http.put("/api/v1/organizations/current", b)),
  getMembers: () => unwrap<Member[]>(http.get("/api/v1/members")),
  invite: (email: string, role: string) => unwrap<{ invitation: Member }>(http.post("/api/v1/members/invite", { email, role })),
};
